import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download, Lock, Mail, Phone, Search } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  FieldLabel,
  StatusPill,
  ToolBody,
  ToolHeader,
  ToolPage,
  primaryButtonClass,
  secondaryButtonClass,
  textareaClass,
} from "./ToolPageChrome";

export default function CandidateSearch({ user }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [recentQueries, setRecentQueries] = useState([]);
  const [searchIndexSummary, setSearchIndexSummary] = useState(null);
  const canUseTalentSearch = user?.plan === "pro" || user?.plan === "pilot" || user?.plan === "enterprise";

  const fetchRecentQueries = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/candidate_search/history?user_id=${user.id}&limit=10`,
        {
          credentials: "include",
        }
      );
      const data = await res.json();

      if (res.ok) {
        setRecentQueries(data.queries || []);
      }
    } catch (err) {
      console.error("Failed to load candidate search history", err);
    }
  };

  useEffect(() => {
    if (user?.id) fetchRecentQueries();
  }, [user?.id]);

  const fetchSearchIndexSummary = async () => {
    if (!user?.id || !canUseTalentSearch) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/candidates/search-index-summary`, {
        credentials: "include",
      });
      const data = await res.json();

      if (res.ok) setSearchIndexSummary(data);
    } catch (err) {
      console.error("Failed to load search index summary", err);
    }
  };

  useEffect(() => {
    fetchSearchIndexSummary();

    if (!searchIndexSummary || searchIndexSummary.complete) return undefined;

    const intervalId = window.setInterval(fetchSearchIndexSummary, 5000);
    return () => window.clearInterval(intervalId);
  }, [user?.id, canUseTalentSearch, searchIndexSummary?.complete]);

  const handleSearch = async () => {
    if (!canUseTalentSearch) {
      setError("AI Talent Search is available on the Pro, Pilot, or Enterprise plan.");
      return;
    }

    if (!query.trim()) {
      setError("Ask a question to find matching candidates.");
      return;
    }

    const formData = new FormData();
    formData.append("query", query);
    formData.append("user_id", user.id);

    try {
      setLoading(true);
      setError("");
      setResults(null);

      const res = await fetch(`${process.env.REACT_APP_API_URL}/candidate_search`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Candidate search failed");

      setResults(data);
      fetchRecentQueries();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const candidates = results?.candidates || [];

  const getCandidateContact = (candidate) => {
    const contactInfo =
      candidate.contact_info && typeof candidate.contact_info === "object"
        ? candidate.contact_info
        : {};

    return {
      email: contactInfo.email || candidate.email || candidate.candidate_email || "",
      phone: contactInfo.phone || candidate.phone || candidate.candidate_phone || "",
    };
  };

  const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const highlightTerms = [
    query,
    ...(results?.query?.requirements || []).map((requirement) => requirement.value)
  ]
    .flatMap((term) => (term || "").split(/\s+/))
    .map((term) => term.trim())
    .filter((term) => term.length > 2);

  const highlightText = (text) => {
    if (!text || highlightTerms.length === 0) return text;

    const pattern = new RegExp(`(${highlightTerms.map(escapeRegExp).join("|")})`, "gi");

    return text.split(pattern).map((part, index) => {
      const shouldHighlight = highlightTerms.some(
        (term) => part.toLowerCase() === term.toLowerCase()
      );

      if (!shouldHighlight) return part;

      return (
        <mark key={index} className="bg-amber-100 text-amber-900 px-1 rounded">
          {part}
        </mark>
      );
    });
  };

  /* ---------- PDF EXPORT ---------- */
  const exportPDF = () => {
    if (candidates.length === 0) {
      alert("Search candidates first");
      return;
    }

    const doc = new jsPDF();

    const tableData = candidates.map((c, index) => {
      const contact = getCandidateContact(c);

      return [
      index + 1,
      c.filename,
      `${Math.round(c.semantic_score || 0)}%`,
      contact.email,
      contact.phone,
      (c.matching_chunks || []).slice(0, 2).join(", ")
      ];
    });

    doc.text("AI Talent Search Results", 14, 10);

    autoTable(doc, {
      head: [[
        "Rank",
        "Name",
        "Search Relevance",
        "Email",
        "Phone",
        "Matching Evidence"
      ]],
      body: tableData,
    });

    doc.save("candidate_search_results.pdf");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full"
    >
      <ToolPage>
        <ToolHeader
          icon={Search}
          title="AI Talent Search"
          description="Ask natural-language questions and find matching candidates from your uploaded CV library."
          meta={results && <StatusPill tone="blue">{results.total || 0} matches</StatusPill>}
        />

        <ToolBody className="space-y-5">
        {!canUseTalentSearch && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            <div className="flex items-start gap-3">
              <Lock size={18} className="mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-amber-950">AI Talent Search is a Pro feature</p>
                <p className="mt-1">
                  Preview the search workflow here. Upgrade to Pro to search your candidate pool with natural-language queries.
                </p>
              </div>
            </div>
          </div>
        )}
        {canUseTalentSearch && searchIndexSummary && !searchIndexSummary.complete && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            <div className="flex items-start gap-3">
              <Search size={18} className="mt-0.5 shrink-0 animate-pulse" />
              <div>
                <p className="font-semibold text-amber-950">AI Talent Search index is updating</p>
                <p className="mt-1">
                  {searchIndexSummary.ready} / {searchIndexSummary.total} candidates are ready for semantic search.
                  Newly imported candidates can still be scanned while indexing finishes.
                </p>
              </div>
            </div>
          </div>
        )}
        {recentQueries.length > 0 && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <p className="text-sm font-semibold text-slate-700 mb-2">
              Recent questions
            </p>

            <div className="flex flex-wrap gap-2">
              {recentQueries.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setQuery(item.query)}
                  className="bg-white border border-slate-200 text-slate-700 px-3 py-1 rounded-md text-sm hover:bg-slate-100"
                >
                  {item.query}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <FieldLabel>Search Question</FieldLabel>
        <textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder='Ask a question, e.g. "Find candidates strong in AWS"'
          rows={8}
          disabled={!canUseTalentSearch}
          className={`${textareaClass} resize-none`}
        />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <button
          onClick={handleSearch}
          disabled={!canUseTalentSearch || loading}
          className={primaryButtonClass}
        >
          <Search size={16} />
          {loading ? "Searching..." : "Find Candidates"}
        </button>

      {loading && (
        <div className="flex flex-col items-center mt-8">
          <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-3 text-sm text-gray-600 font-medium">
            Searching candidate vectors...
          </p>
        </div>
      )}

      {results && !loading && (
        <div className="mt-8 space-y-4">
          {candidates.length === 0 ? (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center text-slate-500">
              No matching candidates found.
            </div>
          ) : (
            <>
              {/* ACTION BAR */}
              <div className="flex justify-between items-center mb-4">
                <span className="text-sm font-medium text-slate-600">
                  {candidates.length} candidates found
                </span>

                {/* Export Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={exportPDF}
                    className={secondaryButtonClass}
                  >
                    <Download size={16} />
                    Export PDF
                  </button>
                </div>
              </div>

              {candidates.map((candidate, index) => {
                const contact = getCandidateContact(candidate);

                return (
                  <div
                    key={candidate.scan_id}
                    className="bg-white border border-slate-200 rounded-lg p-5 hover:border-slate-300 transition"
                  >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div>
                      <span className="bg-slate-100 text-slate-700 px-2 py-1 rounded-md font-semibold text-sm">
                        Result #{index + 1}
                      </span>
                      <h3 className="text-lg font-semibold text-slate-950 mt-2">
                        {candidate.filename || "Candidate"}
                      </h3>

                      <div className="flex flex-wrap gap-2 mt-3">
                        {contact.email ? (
                          <a
                            href={`mailto:${contact.email}?subject=${encodeURIComponent("Recruitment opportunity")}`}
                            className="inline-flex items-center gap-1.5 bg-slate-900 text-white px-3 py-1 rounded-md text-sm font-medium hover:bg-slate-800"
                          >
                            <Mail size={14} />
                            Email
                          </a>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-400 border border-slate-200 px-3 py-1 rounded-md text-sm font-medium">
                            <Mail size={14} />
                            Email unavailable
                          </span>
                        )}

                        {contact.phone ? (
                          <span
                            className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 rounded-md text-sm font-medium"
                          >
                            <Phone size={14} />
                            {contact.phone}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-400 border border-slate-200 px-3 py-1 rounded-md text-sm font-medium">
                            <Phone size={14} />
                            Phone unavailable
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-slate-950 font-semibold text-xl">
                        {Math.round(candidate.semantic_score || 0)}%
                      </div>
                      <div className="text-xs text-slate-500">
                        Search relevance
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg p-4">
                    {(candidate.evidence_status || candidate.matched_requirements?.length > 0) && (
                      <div className="mb-4 flex flex-wrap gap-2">
                        {candidate.evidence_status && (
                          <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
                            {candidate.evidence_status.replace("_", " ")}
                          </span>
                        )}

                        {(candidate.matched_requirements || []).map((requirement, idx) => (
                          <span
                            key={idx}
                            className="bg-slate-100 text-slate-700 border border-slate-200 px-3 py-1 rounded-md text-xs font-medium"
                          >
                            {requirement}
                          </span>
                        ))}
                      </div>
                    )}

                    <h4 className="font-semibold text-slate-800 mb-3">
                      Matching CV Sections
                    </h4>

                    <div className="space-y-3">
                      {(candidate.matching_chunks || []).map((chunk, chunkIndex) => (
                        <p
                          key={`${candidate.scan_id}-${chunkIndex}`}
                          className="text-sm text-slate-700 leading-relaxed bg-white border border-slate-200 rounded-lg p-3"
                        >
                          {highlightText(chunk)}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
                );
              })}
            </>
          )}
        </div>
      )}
        </ToolBody>
      </ToolPage>
    </motion.div>
  );
}
